import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  FileClock, Briefcase, Send, Trophy, XOctagon,
  AlertTriangle, ArrowUpRight, Info, FilePlus,
} from "lucide-react";

// Legacy chip — preserved for non-regression. Tenders list still imports it.
export function StatusChip({ status }) {
  const map = {
    active:    "border-brand bg-brand-soft text-brand",
    submitted: "border-ink-line bg-secondary text-ink-muted",
    won:       "border-good bg-good-soft text-good",
    lost:      "border-warn bg-warn-soft text-warn",
  };
  const cls = map[status] || "border-ink-line text-ink-muted";
  return <span className={`chip ${cls} text-[10px]`}>{(status || "active").toUpperCase()}</span>;
}

// New 5-state lifecycle chip — dashboard_restructure_spec §2.
export function StateChip({ state }) {
  const s = (state || "OPEN").toUpperCase();
  const map = {
    OPEN:      "border-ink-line bg-secondary text-ink-muted",
    ACTIVE:    "border-brand/40 bg-brand-soft text-brand",
    SUBMITTED: "border-ink bg-ink text-white",
    WON:       "border-good/40 bg-good-soft text-good",
    LOST:      "border-warn/40 bg-warn-soft text-warn",
  };
  return <span data-testid={`state-chip-${s.toLowerCase()}`} className={`chip text-[10px] font-display font-bold tracking-tight ${map[s] || map.OPEN}`}>{s}</span>;
}

// Overall Score chip — composite metric. dashboard_restructure_spec §4.
export function OverallScoreChip({ tenderId, compact = false }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!tenderId) return;
    api.get(`/tenders/${tenderId}/overall-score`)
       .then(({ data }) => setData(data))
       .catch(() => setData({ overall: null, missing: ["BNBD", "PCAB", "PCEI"] }));
  }, [tenderId]);
  if (!data) return null;
  const o = data.overall;
  if (!o) {
    return (
      <span data-testid="overall-score-chip-missing" title={`Complete ${(data.missing || []).join(", ")} to see Overall Score`}
        className="chip border-ink-line bg-secondary text-ink-muted text-[10px] font-num">
        Overall: —
      </span>
    );
  }
  const colorClass =
    o.color === "dark_green" || o.color === "green" ? "border-good/40 bg-good-soft text-good"
    : o.color === "amber" ? "border-warn/40 bg-warn-soft text-warn"
    : o.color === "orange" || o.color === "red" ? "border-warn/40 bg-warn-soft text-warn"
    : "border-brand/40 bg-brand-soft text-brand";
  return (
    <span data-testid="overall-score-chip" title={`BNBD ${data.inputs.bnbd}, PCAB ${data.inputs.pcab}, PCEI ${data.inputs.pcei}`}
      className={`chip text-[10px] font-num font-bold tracking-tight ${colorClass}`}>
      {compact ? `${o.score}` : `Overall: ${o.score}`}
    </span>
  );
}

const STATE_META = {
  open:      { label: "Open",      icon: FileClock, color: "border-ink-line",       accent: "text-ink" },
  active:    { label: "Active",    icon: Briefcase, color: "border-brand",          accent: "text-brand" },
  submitted: { label: "Submitted", icon: Send,      color: "border-ink",            accent: "text-ink" },
  won:       { label: "Won",       icon: Trophy,    color: "border-good",           accent: "text-good" },
  lost:      { label: "Lost",      icon: XOctagon,  color: "border-warn",           accent: "text-warn" },
};

const TIER_PILL = {
  red: "bg-warn-soft text-warn border-warn/40",
  orange: "bg-warn-soft text-warn border-warn/40",
  amber: "bg-warn-soft text-warn border-warn/40",
  light_green: "bg-good-soft text-good border-good/40",
  dark_green: "bg-good-soft text-good border-good/40",
};

function fmtMoney(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n) || n <= 0) return "—";
  if (n >= 10_000_000) return `₹ ${(n / 10_000_000).toFixed(2)} Cr`;
  if (n >= 100_000)    return `₹ ${(n / 100_000).toFixed(2)} L`;
  return `₹ ${n.toLocaleString("en-IN")}`;
}

function daysLeftLabel(d) {
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d < 0)   return `closed ${Math.abs(d)}d ago`;
  return `${String(d).padStart(2, "0")} days`;
}

function KpiCard({ stateKey, value, sub, to }) {
  const m = STATE_META[stateKey];
  const Icon = m.icon;
  return (
    <Link to={to} data-testid={`kpi-${stateKey}`}
      className={`card-flat p-5 border-l-4 ${m.color} hover:-translate-y-0.5 hover:shadow-md transition-all block`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="stat-label">{m.label}</div>
          <div className={`font-num text-[40px] leading-none font-bold mt-3 ${m.accent}`}>{value ?? 0}</div>
          {sub && <div className="text-[11px] text-ink-muted mt-2 uppercase tracking-wider">{sub}</div>}
        </div>
        <div className={`w-9 h-9 grid place-items-center rounded-sm ${m.accent} bg-secondary`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

function PipelineDistribution({ rows, totalActive }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="card-flat p-6" data-testid="dashboard-distribution">
      <div className="flex items-center justify-between">
        <div className="stat-label">Pipeline Distribution</div>
        <div className="text-[11px] uppercase tracking-wider text-ink-muted">Active by BNBD tier</div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-ink-muted mt-6">No Active tenders yet. Complete a BNBD with score ≥ 65 to auto-promote a tender into the pipeline.</div>
      ) : (
        <div className="mt-5 space-y-3">
          {rows.map((r) => {
            const pct = (r.count / max) * 100;
            return (
              <div key={r.tier_label}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{r.tier_label}</span>
                  <span className="font-num text-ink-muted">{r.count}</span>
                </div>
                <div className="h-2 bg-secondary rounded-sm overflow-hidden mt-1">
                  <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          <div className="text-[11px] uppercase tracking-wider text-ink-muted pt-2 border-t border-ink-line font-num mt-3">
            {totalActive} total Active tender{totalActive === 1 ? "" : "s"}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const winRate = useMemo(() => {
    if (!stats?.win_rate) return null;
    return stats.win_rate;
  }, [stats]);

  if (loading) return <div className="text-sm text-ink-muted uppercase tracking-wider">Loading dashboard…</div>;
  if (!stats) return <div className="text-sm text-ink-muted">Unable to load dashboard.</div>;

  const kpi = stats.kpi || {};
  const pv = stats.pipeline_value || {};
  const totalTenders = (kpi.open || 0) + (kpi.active || 0) + (kpi.submitted || 0) + (kpi.won || 0) + (kpi.lost || 0);
  const isEmpty = totalTenders === 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="border-b border-ink-line pb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Pipeline Snapshot</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Dashboard</h1>
          <p className="text-sm text-ink-muted mt-1.5">Your tender pipeline across the 5-stage lifecycle.</p>
        </div>
        <Link to="/tenders/new" data-testid="dashboard-add-tender-btn"
          className="px-4 py-2 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm transition-colors">
          + Add Tender
        </Link>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="dashboard-kpi-strip">
        <KpiCard stateKey="open" value={kpi.open} to="/tenders?state=OPEN" />
        <KpiCard stateKey="active" value={kpi.active} to="/tenders?state=ACTIVE" />
        <KpiCard stateKey="submitted" value={kpi.submitted} to="/tenders?state=SUBMITTED" />
        <KpiCard stateKey="won" value={kpi.won} to="/tenders?state=WON" />
        <KpiCard stateKey="lost" value={kpi.lost} to="/tenders?state=LOST" />
      </div>

      {isEmpty ? (
        /* Empty state for brand-new users — peruser_tender_isolation_spec §3.2 */
        <div className="card-flat px-8 py-16 text-center" data-testid="dashboard-empty-state">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-brand-soft text-brand mb-5">
            <FilePlus className="w-6 h-6" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">You haven&apos;t added any tenders yet.</h2>
          <p className="text-sm text-ink-muted mt-3 max-w-md mx-auto">
            Start tracking your pipeline by adding your first tender. Your dashboard,
            assessments, and win-rate metrics will populate from here.
          </p>
          <Link to="/tenders/new" data-testid="dashboard-empty-add-first-btn"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover text-white font-medium rounded-sm transition-colors">
            <FilePlus className="w-4 h-4" /> Add Your First Tender
          </Link>
        </div>
      ) : (
        <>
      {/* Pipeline value + Win rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat p-6" data-testid="dashboard-pipeline-value">
          <div className="stat-label">Pipeline Value</div>
          <div className="font-num text-5xl font-bold tracking-tight mt-3 text-brand">
            {fmtMoney(pv.total)}
          </div>
          <div className="text-[12px] text-ink-muted mt-2">
            {pv.active_count || 0} Active tender{pv.active_count === 1 ? "" : "s"} with BNBD ≥ approval threshold
          </div>
          {(pv.excluded_count || 0) > 0 && (
            <div className="mt-3 text-[11px] flex items-start gap-1.5 text-ink-muted">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{pv.excluded_count} tender{pv.excluded_count === 1 ? "" : "s"} excluded — contract value not disclosed.</span>
            </div>
          )}
        </div>

        <div className="card-flat p-6" data-testid="dashboard-win-rate">
          <div className="stat-label">Win Rate</div>
          {winRate?.submitted_total ? (
            <>
              <div className={`font-num text-5xl font-bold tracking-tight mt-3 ${winRate.pct >= 50 ? "text-good" : winRate.pct >= 25 ? "text-brand" : "text-ink"}`}>
                {winRate.pct}%
              </div>
              <div className="text-[12px] text-ink-muted mt-2 font-num">
                {winRate.won} won / {winRate.submitted_total} submitted
              </div>
            </>
          ) : (
            <>
              <div className="font-num text-5xl font-bold tracking-tight mt-3 text-ink-muted">—</div>
              <div className="text-[12px] text-ink-muted mt-2">No tenders submitted yet.</div>
            </>
          )}
        </div>
      </div>

      <PipelineDistribution rows={stats.distribution || []} totalActive={kpi.active || 0} />

      {/* Upcoming + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat" data-testid="dashboard-upcoming">
          <div className="px-6 py-4 border-b border-ink-line flex items-center justify-between">
            <div>
              <div className="stat-label">Upcoming Deadlines</div>
              <div className="text-[11px] text-ink-muted uppercase tracking-wider mt-0.5">Active tenders nearing close</div>
            </div>
            <Link to="/tenders?state=ACTIVE" className="text-[12px] uppercase tracking-wider text-brand hover:text-brand-hover">View all →</Link>
          </div>
          {(stats.upcoming_deadlines || []).length === 0 ? (
            <div className="px-6 py-6 text-sm text-ink-muted">No upcoming deadlines.</div>
          ) : (
            <ul className="divide-y divide-ink-line">
              {(stats.upcoming_deadlines || []).map((t) => {
                const urgent = t.days_left <= 3;
                return (
                  <li key={t.id} className="px-6 py-3">
                    <Link to={`/tenders/${t.id}`} className="flex items-center justify-between gap-3 group">
                      <div className="min-w-0">
                        <div className="font-medium truncate text-sm">{t.title || "—"}</div>
                        <div className="text-[11px] text-ink-muted truncate">{t.client || "—"}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {t.bnbd_tier_label && (
                          <span className={`chip text-[10px] ${TIER_PILL[t.bnbd_tier_color] || "bg-secondary text-ink-muted border-ink-line"}`}>
                            {t.bnbd_tier_label}
                          </span>
                        )}
                        <span className={`font-num text-xs ${urgent ? "text-warn font-bold flex items-center gap-1" : "text-ink-muted"}`}>
                          {urgent && <AlertTriangle className="w-3 h-3" />}
                          {daysLeftLabel(t.days_left)}
                        </span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-ink-muted group-hover:text-brand transition-colors" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="card-flat" data-testid="dashboard-recent">
          <div className="px-6 py-4 border-b border-ink-line flex items-center justify-between">
            <div>
              <div className="stat-label">Recent Tenders</div>
              <div className="text-[11px] text-ink-muted uppercase tracking-wider mt-0.5">Most recently submitted</div>
            </div>
            <Link to="/tenders?state=SUBMITTED" className="text-[12px] uppercase tracking-wider text-brand hover:text-brand-hover">View all →</Link>
          </div>
          {(stats.recent_tenders || []).length === 0 ? (
            <div className="px-6 py-6 text-sm text-ink-muted">No submitted tenders yet.</div>
          ) : (
            <ul className="divide-y divide-ink-line">
              {(stats.recent_tenders || []).map((t) => {
                const state = (t.state || "SUBMITTED").toUpperCase();
                const stateColor = state === "WON" ? "bg-good-soft text-good border-good/40"
                                  : state === "LOST" ? "bg-warn-soft text-warn border-warn/40"
                                  : "bg-secondary text-ink-muted border-ink-line";
                return (
                  <li key={t.id} className="px-6 py-3">
                    <Link to={`/tenders/${t.id}`} className="flex items-center justify-between gap-3 group">
                      <div className="min-w-0">
                        <div className="font-medium truncate text-sm">{t.title || "—"}</div>
                        <div className="text-[11px] text-ink-muted truncate">{t.client || "—"}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`chip text-[10px] ${stateColor}`}>{state}</span>
                        {t.result_position && <span className="text-[11px] text-ink-muted font-num">{t.result_position}</span>}
                        <ArrowUpRight className="w-3.5 h-3.5 text-ink-muted group-hover:text-brand transition-colors" />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  );
}
